// src/pages/ProjectUniverse.jsx
import { useEffect } from 'react';
import '../styles/ProjectPage.css';
import CaseStudyToc from '../components/CaseStudyToc';
import BackToTop from '../components/BackToTop';
import { projectsData } from '../data/projects';

const TOC_SECTIONS = [
    {
        id: 'recherche-groupe',
        label: 'Recherche',
        children: [
            { id: 'contexte', label: 'Contexte' },
            { id: 'parti-pris', label: 'Le parti pris' }
        ]
    },
    {
        id: 'conception',
        label: 'Conception',
        children: [
            { id: 'decisions', label: "Mes décisions d'interface" },
            { id: 'retour', label: "Ce que j'en retiens" }
        ]
    }
];

export default function ProjectUniverse() {
    const project = projectsData.find((p) => p.id === 'universe');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="project-page">
            <main className="project-main figma-project-main">
                <div className="figma-embed-wrapper">
                    <iframe
                        className="figma-embed"
                        src={project.figmaEmbedSrc}
                        title={`Maquette Figma – ${project.title}`}
                        allowFullScreen
                    />
                </div>

                <div className="project-content">
                    <h1>{project.title}</h1>
                    <p className="project-desc">{project.desc}</p>

                    {project.tech?.length > 0 && (
                        <div className="project-tech">
                            <h3>Compétences</h3>
                            <ul>
                                {project.tech.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="project-actions">
                        <a href={project.figmaUrl} target="_blank" rel="noopener noreferrer" className="back-btn">
                            Ouvrir dans Figma ↗
                        </a>
                    </div>
                </div>
            </main>

            <CaseStudyToc sections={TOC_SECTIONS} />

            <section className="case-study case-study--inline">
                <section className="case-study-section">
                    <h2 id="recherche-groupe">Recherche</h2>

                    <h3 id="contexte">Contexte</h3>
                    <p>
                        Projet de fin d'études commandé par une entreprise partenaire. 67 % des 8-10 ans
                        sont déjà sur les réseaux sociaux, sans qu'aucune plateforme ne soit conçue pour
                        eux. Équipe de cinq, six mois. J'étais responsable de la conception graphique et
                        de la maquette Figma.
                    </p>

                    <h3 id="parti-pris">Le parti pris</h3>
                    <p>
                        La plupart des solutions anti-harcèlement modèrent après coup : un message part,
                        quelqu'un le signale, un modérateur tranche. Le mal est déjà fait.
                    </p>
                    <p>
                        On a inversé : chaque message passe par une analyse avant envoi. S'il est détecté
                        comme violent, il n'est pas bloqué mais renvoyé à son auteur avec une demande de
                        reformulation. Ça change la nature du produit : on ne punit pas, on interrompt.
                    </p>
                </section>
            </section>

            <section className="case-study case-study--continued">
                <section className="case-study-section">
                    <h2 id="conception">Conception</h2>

                    <h3 id="decisions">Mes décisions d'interface</h3>
                    <ul className="decision-list">
                        <li>
                            <strong>Ne pas faire une app « pour enfants ».</strong>
                            Les préados rejettent ce qui leur rappelle qu'on les traite comme des petits.
                            J'ai visé un registre proche des messageries qu'ils utilisent déjà, avec une
                            identité visuelle sobre plutôt que les couleurs primaires saturées des apps
                            jeunesse.
                        </li>
                        <li>
                            <strong>Une interface de chat volontairement familière.</strong>
                            La modération est déjà une friction. L'interface ne devait en ajouter aucune :
                            liste de conversations, bulles, envoi. Rien à réapprendre.
                        </li>
                    </ul>

                    <h3 id="retour">Ce que j'en retiens</h3>
                    <p>
                        Ce que j'en retiens, c'est surtout comment penser une app que les ados ont envie
                        d'utiliser sans se sentir surveillés. Un outil trop cadré se reconnaît tout de
                        suite, un peu comme un Teams ou l'ancien chat de Pronote, et pousse directement
                        vers Instagram ou d'autres réseaux. L'entreprise partenaire ne cherchait pas à
                        remplacer les réseaux sociaux existants, mais à proposer une première application
                        pour introduire les jeunes aux usages sociaux en ligne et les accompagner.
                    </p>
                </section>
            </section>

            <BackToTop />
        </div>
    );
}
